import './midtermTransaction.scss';
import './cancellation-details.scss';

import {
  CreateMidtermTransactionField,
  getMidtermTransactionFieldProps,
} from '@converge/data';

import {
  Box,
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography,
} from '@mui/material';
import { NewDateField } from '../../../../presentation/components';
import { CancellationSubType, RefundType } from '@converge/graphql';
import { getLabelFromEnum } from '../../utils';
import { useFormContext } from 'react-hook-form';
import { useEffect, useMemo, useRef } from 'react';
import moment from 'moment';

export interface CancellationDetailsProps {
  setIsDisabled: React.Dispatch<React.SetStateAction<boolean>>;
  effectiveDate?: string;
  maxDate?: Date;
}

export const CancellationDetails = ({
  setIsDisabled,
  effectiveDate,
  maxDate,
}: CancellationDetailsProps) => {
  const isFirstRender = useRef(true);
  const prevCancellationType = useRef<CancellationSubType | undefined>(
    undefined
  );
  const {
    watch,
    setValue,
    formState: { errors },
  } = useFormContext();
  const [cancellationType, refundType, startDate] = watch([
    CreateMidtermTransactionField.cancellationType,
    CreateMidtermTransactionField.refundType,
    CreateMidtermTransactionField.startDate,
  ]);

  useEffect(() => {
    const hasRefundType =
      refundType &&
      !(
        cancellationType !== CancellationSubType.FlatCancellation &&
        refundType === RefundType.FlatCancel
      );
    const hasErrors = Object.keys(errors).length > 0;
    const allFilled = cancellationType && hasRefundType && startDate;
    setIsDisabled(!allFilled || hasErrors);
  }, [cancellationType, refundType, startDate, errors, setIsDisabled]);

  const REFUND_TYPE_OPTIONS = useMemo(() => {
    return Object.values(RefundType)
      .filter(
        (type) =>
          type !== RefundType.FlatCancel ||
          cancellationType === CancellationSubType.FlatCancellation
      )
      .map((type) => ({
        label: getLabelFromEnum(type),
        value: type,
      }));
  }, [cancellationType]);

  useEffect(() => {
    if (isFirstRender.current) {
      if (cancellationType === CancellationSubType.FlatCancellation) {
        setValue(
          CreateMidtermTransactionField.refundType,
          RefundType.FlatCancel
        );
        setValue(CreateMidtermTransactionField.startDate, effectiveDate);
      } else if (cancellationType && !startDate) {
        setValue(
          CreateMidtermTransactionField.startDate,
          moment().utc().format()
        );
      }
      isFirstRender.current = false;
    } else if (cancellationType !== prevCancellationType.current) {
      if (cancellationType === CancellationSubType.FlatCancellation) {
        setValue(
          CreateMidtermTransactionField.refundType,
          RefundType.FlatCancel
        );
        setValue(CreateMidtermTransactionField.startDate, effectiveDate);
      } else {
        setValue(CreateMidtermTransactionField.refundType, RefundType.ProRata);
        setValue(
          CreateMidtermTransactionField.startDate,
          moment().utc().format()
        );
      }
    }
    prevCancellationType.current = cancellationType;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cancellationType, effectiveDate, setValue]);

  const getCancellationTypeOptions = (type: CancellationSubType) => {
    switch (type) {
      case CancellationSubType.FlatCancellation:
        return 'Flat Cancellation';
      case CancellationSubType.NonPayment:
        return 'Non-Payment of Premium';
      case CancellationSubType.InsuredRequest:
        return 'Requested by Insured';
      default:
        return 'Underwriting Reasons';
    }
  };
  const cancellationTypeOptions = Object.values(CancellationSubType).map(
    (type) => ({
      label: getCancellationTypeOptions(type),
      value: type,
    })
  );
  const CANCELLATION_TYPE_OPTIONS = cancellationTypeOptions.map((item) => ({
    label: item?.label || '',
    value: item?.value || '',
  }));

  const isRefundAndDateDisabled =
    !cancellationType ||
    cancellationType === CancellationSubType.FlatCancellation;
  const isTitleDisabled = !cancellationType;

  const effectiveDateUTC = moment.utc(effectiveDate);
  const minDate = new Date(
    effectiveDateUTC.year(),
    effectiveDateUTC.month(),
    effectiveDateUTC.date()
  );
  return (
    <Box
      className="cancellation-details"
      sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}
    >
      <Box>
        <Typography className="cancellation-details__section-title">
          What type of cancellation?
        </Typography>
        <RadioGroup
          data-testid="cancellation-type-radio-group"
          value={cancellationType ?? ''}
          onChange={(e) => {
            setValue(
              CreateMidtermTransactionField.cancellationType,
              e.target.value as CancellationSubType
            );
          }}
        >
          {CANCELLATION_TYPE_OPTIONS.map((opt) => (
            <FormControlLabel
              key={opt.value}
              value={opt.value}
              control={
                <Radio
                  inputProps={
                    {
                      'data-testid': `${opt.value
                        .toLowerCase()
                        .replace(/_/g, '-')}-cancellation-type-radio`,
                    } as React.InputHTMLAttributes<HTMLInputElement>
                  }
                  sx={{
                    color: '#4d4d4d',
                    '&.Mui-checked': { color: '#28C0A7' },
                  }}
                />
              }
              label={opt.label}
              sx={{}}
            />
          ))}
        </RadioGroup>
      </Box>

      <Box>
        <Typography
          className={`cancellation-details__section-title${
            isTitleDisabled
              ? ' cancellation-details__section-title--disabled'
              : ''
          }`}
        >
          What type of refund?
        </Typography>
        <RadioGroup
          data-testid="refund-type-radio-group"
          value={refundType ?? ''}
          onChange={(e) => {
            setValue(
              CreateMidtermTransactionField.refundType,
              e.target.value as RefundType
            );
          }}
        >
          {REFUND_TYPE_OPTIONS.map((opt) => (
            <FormControlLabel
              key={opt.value}
              value={opt.value}
              disabled={isRefundAndDateDisabled}
              control={
                <Radio
                  inputProps={
                    {
                      'data-testid': `${opt.value
                        .toLowerCase()
                        .replace(/_/g, '-')}-refund-type-radio`,
                    } as React.InputHTMLAttributes<HTMLInputElement>
                  }
                  sx={{
                    color: '#4d4d4d',
                    '&.Mui-checked': { color: '#28C0A7' },
                  }}
                />
              }
              label={opt.label}
              sx={{}}
            />
          ))}
        </RadioGroup>
      </Box>

      <Box>
        <Typography
          className={`cancellation-details__section-title${
            isTitleDisabled
              ? ' cancellation-details__section-title--disabled'
              : ''
          }`}
        >
          What is the effective date?
        </Typography>
        <Box style={{ width: '300px', paddingBottom: '80px' }}>
          <NewDateField
            {...getMidtermTransactionFieldProps(
              CreateMidtermTransactionField.startDate
            )}
            data-testid="cancellation-effective-date-input"
            minDate={minDate}
            maxDate={maxDate}
            disabled={isRefundAndDateDisabled}
          />
        </Box>
      </Box>
    </Box>
  );
};
